import React, { useEffect, useState } from "react";
import { Modal, Table, DatePicker } from "antd";
import moment from "moment";
import { toast } from "react-toastify";
import { fetchTestHistoryQCData } from "../../../../actions/qc";
import useAbortController from "../../../Common/hooks/useAbortController";
import "./MicroPendingHistoryModal.css";
import { ML_SOFTWARE_QC_FLAGS } from "../../../../utils/statusConfig";
import StatusTag from "../../../Common/components/StatusTag";

const columns = [
  { title: "Sample ID", dataIndex: "sample_id", key: "sample_id" },
  { title: "Sample Type", dataIndex: "sample_type", key: "sample_type" },
  { title: "Result", dataIndex: "result", key: "result" },
  {
    title: "QC", dataIndex: "ml_soft_qc_status", key: "ml_soft_qc_status", qcFlagKey: "ml_soft_qc_result",
  },
  {
    title: "AI QC", dataIndex: "ml_qc_status", key: "ml_qc_status", qcFlagKey: "ml_qc_result",
  },
  {
    title: "Software QC", dataIndex: "qc_status", key: "qc_status", qcFlagKey: "qc_result",
  },
  {
    title: "Manual QC", dataIndex: "manual_qc_status", key: "manual_qc_status", qcFlagKey: "manual_qc_result",
  },
];

const HistoryQCTableRow = ({
  record,
  ...antdProps
}) => {
  if (!record) {
    return <tr {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }

  return (
    <tr
      {...antdProps} // eslint-disable-line
      className="MicroHistoryTableQC"
    />
  );
};

const HistoryQCTableCell = ({
  record,
  dataIndex,
  qcFlagKey,
  ...antdProps
}) => {
  if (!record) {
    return <td {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }

  const getStyles = (result) => ({ backgroundColor: ML_SOFTWARE_QC_FLAGS[result || "-"]?.style?.backgroundColor });

  const childNode = record?.[dataIndex] || "-";
  let style = null;
  if (qcFlagKey) {
    style = getStyles(record?.[qcFlagKey]);
  }
  return (<td {...antdProps} style={style}>{childNode}</td>)// eslint-disable-line
};

const MicroPendingHistoryModal = ({
  onCancel,
  parentData,
}) => {
  const [historyInfo, setHistoryInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState();
  const { setNewAbortController } = useAbortController();

  const formatDateRange = (input, format = "YYYY-MM-DD") => {
    if (!input || input.length < 2) return "";

    const startDate = moment(input[0].$d).format(format);
    const endDate = moment(input[1].$d).format(format);

    return `${startDate} - ${endDate}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, message, result, canceled,
    } = await fetchTestHistoryQCData({
      wo_id_pk: parentData.wo,
      genista_id: parentData.genista_id,
      analysis: parentData.analysis,
      date_range: formatDateRange(dateRange),
    }, newAbortController.signal);

    if (success && result.samples && result.samples.length > 0) {
      setData(result.samples.map((item, key) => ({
        ...item, key, analysis: parentData.analysis, petrifilm: parentData.petrifilm,
      })));
      setHistoryInfo(result);
    } else if (!canceled) {
      toast.error(message);
      setData([]);
    }
    setLoading(canceled);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]); // eslint-disable-line

  return (
    <Modal
      open
      title={`History Data: ${formatDateRange(dateRange, "MM/DD/YYYY")}`}
      onCancel={onCancel}
      footer={null}
      rootClassName="micro-history-modal"
      centered
      width={850}
    >
      <div className="micro-history-info-wrapper">
        <div className="MicroHistoryModal__Section">
          <h4 className="MicroHistoryModal__SectionTitle">Sample Info</h4>
          <div className="MicroHistoryModal__Details">
            <div className="MicroHistoryModal__Description">
              <h6>WO:</h6>
              <p>{parentData.wo_id || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Genista ID:</h6>
              <p>{parentData.genista_id || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>QC:</h6>
              <StatusTag result={historyInfo.ml_soft_qc_result} type="QC" />
            </div>
            <div className="MicroHistoryModal__Description width-full">
              <h6>Sample ID:</h6>
              <p>{historyInfo.sample_id || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description width-full">
              <h6>Sample Type:</h6>
              <p>{historyInfo.sample_type || "-"}</p>
            </div>
          </div>
        </div>
        <div className="MicroHistoryModal__Section width-full">
          <h4 className="MicroHistoryModal__SectionTitle">Analysis Info</h4>
          <div className="MicroHistoryModal__SectionDescriptionRow">
            <div className="MicroHistoryModal__Description">
              <h6>Analysis:</h6>
              <p>{parentData.analysis || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Petrifilm:</h6>
              <p>{parentData.petrifilm || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>DF:</h6>
              <p>{parentData.df || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Result:</h6>
              <p>{historyInfo.parsed_result || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Lower Bound:</h6>
              <p>{historyInfo.lower_bound || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Upper Bound:</h6>
              <p>{historyInfo.upper_bound || "-"}</p>
            </div>
            <div className="MicroHistoryModal__Description">
              <h6>Message:</h6>
              <p>{historyInfo.message || "-"}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="micro-history-section-title-wrapper">
        <div className="micro-history-section-title">Sample Historical Data:</div>
        <DatePicker.RangePicker
          className="MicroPendingTable__HistoryDate"
          format="MM/DD/YYYY"
          value={dateRange}
          onChange={setDateRange}
        />
      </div>
      <Table
        bordered
        sticky
        className="micro-history-table"
        loading={loading}
        dataSource={data}
        pagination={false}
        components={{
          body: {
            row: HistoryQCTableRow,
            cell: HistoryQCTableCell,
          },
        }}
        onRow={(record, index) => (
          { record, index }
        )}
        columns={columns.map((col) => ({
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
            qcFlagKey: col.qcFlagKey,
          }),
        }))}
      />
    </Modal>
  );
};

export default MicroPendingHistoryModal;
